import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, Music, Loader2, CheckCircle } from 'lucide-react';
import { trackRequestClick, trackRequestSubmit } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const CHART_OPTIONS = [
  { value: 'top_20_songs', label: 'Top 20 Songs', description: 'Main chart for top tracks' },
  { value: 'top_20_artists', label: 'Top 20 Artists', description: 'Featured artist chart' },
  { value: 'rising', label: 'Rising', description: 'Up-and-coming tracks' },
  { value: 'new_releases', label: 'New Releases', description: 'Recently released music' },
  { value: 'genre', label: 'Genre Chart', description: 'Chart specific to a genre' },
  { value: 'region', label: 'Region Chart', description: 'Chart specific to DMV region' },
];

const GENRES = [
  'Hip-Hop/Rap', 'R&B/Soul', 'Pop', 'Rock', 'Electronic/EDM', 'Jazz', 
  'Gospel', 'Country', 'Latin', 'Alternative', 'Indie', 'Classical',
  'Go-Go', 'Afrobeats', 'Reggae', 'Other'
];

const DMV_REGIONS = [
  'Washington DC', 'Baltimore', 'Northern Virginia', 'Prince George\'s County',
  'Montgomery County', 'Alexandria', 'Arlington', 'Anne Arundel County'
];

export function ChartRequestModal({ isOpen, onClose, track }) {
  const { getAuthHeaders } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    requested_chart: '',
    genre: track?.genre || '',
    region: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Track request click
    console.log('[ChartRequest] Submit clicked for track:', track?.track_id);
    trackRequestClick('track', track?.track_id);
    
    if (!formData.requested_chart) {
      toast.error('Please select a chart type');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/charts/request-placement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          track_id: track.track_id,
          requested_chart: formData.requested_chart,
          genre: formData.genre || null,
          region: formData.region || null,
          notes: formData.notes || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Track successful submission
        trackRequestSubmit('track', track.track_id, true);
        console.log('[ChartRequest] Request submitted successfully');
        
        setSuccess(true);
        toast.success('Chart request submitted successfully!');
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setFormData({ requested_chart: '', genre: track?.genre || '', region: '', notes: '' });
        }, 2000);
      } else {
        // Track failed submission
        trackRequestSubmit('track', track.track_id, false);
        console.log('[ChartRequest] Request failed:', data.detail);
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (err) {
      // Track error
      trackRequestSubmit('track', track?.track_id, false);
      console.error('[ChartRequest] Error submitting request:', err);
      toast.error('Failed to submit chart request');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedChart = CHART_OPTIONS.find(c => c.value === formData.requested_chart);
  const showGenre = formData.requested_chart === 'genre' || formData.requested_chart === 'top_20_songs';
  const showRegion = formData.requested_chart === 'region';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="chart-request-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-secondary" />
            Request Top 20 Placement
          </DialogTitle>
          <DialogDescription>
            Submit your track for consideration on DMV Soundstage charts
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Request Submitted!</h3>
            <p className="text-muted-foreground">
              Our team will review your submission and notify you of the decision.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Track Info */}
            <div className="p-3 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded bg-muted overflow-hidden flex-shrink-0">
                  {track?.cover_art_url ? (
                    <img src={track.cover_art_url} alt={track.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/30 to-secondary/30">
                      <Music className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-medium">{track?.title || 'Unknown Track'}</p>
                  <p className="text-sm text-muted-foreground">{track?.artist_name || 'Unknown Artist'}</p>
                </div>
              </div>
            </div>

            {/* Chart Type Selection */}
            <div className="space-y-2">
              <Label>Chart Type *</Label>
              <Select
                value={formData.requested_chart}
                onValueChange={(value) => setFormData({ ...formData, requested_chart: value })}
              >
                <SelectTrigger data-testid="chart-type-select">
                  <SelectValue placeholder="Select a chart" />
                </SelectTrigger>
                <SelectContent>
                  {CHART_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">- {option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedChart && (
                <p className="text-xs text-muted-foreground">{selectedChart.description}</p>
              )}
            </div>

            {/* Genre Selection */}
            {showGenre && (
              <div className="space-y-2">
                <Label>Primary Genre {formData.requested_chart === 'genre' ? '*' : ''}</Label>
                <Select
                  value={formData.genre}
                  onValueChange={(value) => setFormData({ ...formData, genre: value })}
                >
                  <SelectTrigger data-testid="genre-select">
                    <SelectValue placeholder="Select genre" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENRES.map(genre => (
                      <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Region Selection */}
            {showRegion && (
              <div className="space-y-2">
                <Label>Region *</Label>
                <Select
                  value={formData.region}
                  onValueChange={(value) => setFormData({ ...formData, region: value })}
                >
                  <SelectTrigger data-testid="region-select">
                    <SelectValue placeholder="Select DMV region" />
                  </SelectTrigger>
                  <SelectContent>
                    {DMV_REGIONS.map(region => (
                      <SelectItem key={region} value={region}>{region}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional information about your submission..."
                className="resize-none"
                rows={3}
                maxLength={500}
                data-testid="request-notes"
              />
              <p className="text-xs text-muted-foreground text-right">{formData.notes.length}/500</p>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !formData.requested_chart}
                className="flex-1 bg-secondary hover:bg-secondary/80"
                data-testid="submit-chart-request"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <>Submit Request</>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Requests are reviewed by the DMV Soundstage team. Approval is based on track quality and eligibility.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ChartRequestModal;
